import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, ScanSearch } from 'lucide-react';
import { useFind, type FindType } from '@/api/insights';
import { useScopeFilter } from '@/contexts/ClientScopeContext';
import { PageHeader } from '@/components/PageHeader';
import { FIND_ICON, FIND_LABEL, FIND_ORDER, FindTags, findPath, rankForScope } from '@/components/insights/FindKit';
import { companyPath } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * Record search results (`/insights/find?q=`). Same search as the header Find box, every result,
 * filterable by type. The query lives in the URL so results can be shared.
 */
export default function FindPage() {
  const [params, setParams] = useSearchParams();
  const urlQ = params.get('q') ?? '';
  const [q, setQ] = useState(urlQ);
  const [type, setType] = useState<FindType | 'all'>('all');
  const { companyId } = useScopeFilter();
  useEffect(() => { setQ(urlQ); }, [urlQ]);
  useEffect(() => {
    const t = setTimeout(() => { if (q.trim() !== urlQ) setParams(q.trim() ? { q: q.trim() } : {}, { replace: true }); }, 300);
    return () => clearTimeout(t);
  }, [q, urlQ, setParams]);

  const { data, isFetching, error } = useFind(urlQ);
  const results = useMemo(() => rankForScope(data?.results ?? [], companyId), [data, companyId]);
  const counts = useMemo(() => Object.fromEntries(FIND_ORDER.map((t) => [t, results.filter((r) => r.type === t).length])) as Record<FindType, number>, [results]);
  const shown = type === 'all' ? results : results.filter((r) => r.type === type);

  return (
    <div className="mx-auto w-full max-w-[1100px] px-4 py-4 md:px-6">
      <PageHeader title="Find" isFetching={isFetching}
        subtitle="Devices (Datto RMM, Intune, IT Glue), people (Microsoft 365 users and Autotask contacts), tickets by number and companies." />
      <div className="relative mt-4">
        <ScanSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hostname, serial, name, email, UPN, ticket number…"
          className="h-10 w-full rounded-md border border-border bg-background pl-9 pr-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/30" />
      </div>

      {urlQ.trim().length >= 2 && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {(['all', ...FIND_ORDER] as const).map((t) => (
            <button key={t} type="button" onClick={() => setType(t)}
              className={cn('rounded-full border px-2.5 py-0.5 text-xs', type === t ? 'border-ring bg-brand-soft text-brand' : 'border-border text-muted-foreground hover:text-foreground')}>
              {t === 'all' ? 'All' : FIND_LABEL[t]} <span className="tabular-nums">{t === 'all' ? results.length : counts[t]}</span>
            </button>
          ))}
          {data?.partial.length ? <span className="ml-2 text-xs text-warning">Couldn't search: {data.partial.join(', ')}</span> : null}
        </div>
      )}

      <div className="mt-3 overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        {urlQ.trim().length < 2 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">Type at least two characters. Ticket numbers (T20261012.0028) and ids work too.</div>
        ) : error ? (
          <div className="px-4 py-6 text-sm text-destructive">{error instanceof Error ? error.message : 'Search failed'}</div>
        ) : !data ? (
          <div className="flex items-center gap-2 px-4 py-10 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Searching every module…</div>
        ) : shown.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">Nothing matches “{urlQ}”.</div>
        ) : (
          <ul className="divide-y divide-divider">
            {shown.map((r) => {
              const Icon = FIND_ICON[r.type];
              return (
                <li key={`${r.type}:${r.key}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/40">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand-soft text-brand"><Icon className="h-3.5 w-3.5" /></span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Link to={findPath(r)} className="truncate text-sm font-medium text-foreground hover:underline">{r.title}</Link>
                      <FindTags tags={r.tags} />
                    </div>
                    <div className="truncate text-xs text-muted-foreground">{r.subtitle}</div>
                  </div>
                  <div className="hidden w-56 shrink-0 text-right text-xs sm:block">
                    {r.companyId != null && r.type !== 'company'
                      ? <Link to={companyPath(r.companyId)} className="text-foreground hover:underline">{r.companyName}</Link>
                      : <span className="text-muted-foreground">{r.type === 'company' ? '' : r.companyName ?? 'No company'}</span>}
                    <div className="text-[11px] text-muted-foreground">matched {r.matched}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {data && data.results.length >= 100 && <p className="mt-2 text-xs text-muted-foreground">Showing the best 100 matches. Narrow the search to see others.</p>}
    </div>
  );
}
